import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, TrendingUp, Users, Lightbulb, Wrench, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { KanbanBoardWithDetails, FeedbackItemWithDetails } from '@/types/feedback';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';

interface RoadmapStats {
  total_items: number;
  in_progress: number;
  completed: number;
  completion_rate: number;
}

const Roadmap: React.FC = () => {
  const [board, setBoard] = useState<KanbanBoardWithDetails | null>(null);
  const [stats, setStats] = useState<RoadmapStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadRoadmap();
  }, []);

  const loadRoadmap = async () => {
    setIsLoading(true);

    try {
      // Load public board with items
      const { data: boardData, error: boardError } = await supabase
        .from('kanban_boards')
        .select(`
          *,
          columns:kanban_columns!inner(*)
        `)
        .eq('is_public', true)
        .eq('is_active', true)
        .eq('kanban_columns.is_active', true)
        .order('order_index', { referencedTable: 'kanban_columns' })
        .single();

      if (boardError) throw boardError;

      if (!boardData) {
        throw new Error('No public roadmap found');
      }

      // Load items for the board
      const { data: itemsData, error: itemsError } = await supabase
        .from('feedback_board_items')
        .select(`
          *,
          feedback_item:feedback_items!inner(
            *,
            category:feedback_categories(*),
            status:feedback_statuses(*)
          )
        `)
        .eq('board_id', boardData.id)
        .eq('feedback_items.is_published', true)
        .order('order_index');

      if (itemsError) throw itemsError;

      // Group items by column
      const columnsWithItems = (boardData.columns || []).map(column => ({
        ...column,
        items: (itemsData || [])
          .filter(item => item.column_id === column.id)
          .sort((a, b) => a.order_index - b.order_index),
      }));

      const roadmapBoard = {
        ...boardData,
        columns: columnsWithItems,
      };

      setBoard(roadmapBoard);

      // Calculate stats
      const totalItems = itemsData?.length || 0;
      const completedItems = columnsWithItems
        .filter(col => col.name.toLowerCase().includes('completed') || col.name.toLowerCase().includes('done'))
        .reduce((acc, col) => acc + col.items.length, 0);
      const inProgressItems = columnsWithItems
        .filter(col => col.name.toLowerCase().includes('progress') || col.name.toLowerCase().includes('development'))
        .reduce((acc, col) => acc + col.items.length, 0);

      setStats({
        total_items: totalItems,
        in_progress: inProgressItems,
        completed: completedItems,
        completion_rate: totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0,
      });
    } catch (error: any) {
      console.error('Failed to load roadmap:', error);
      toast({
        title: 'Failed to load roadmap',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getColumnIcon = (columnName: string) => {
    const name = columnName.toLowerCase();
    if (name.includes('backlog') || name.includes('new')) return <Lightbulb className="h-4 w-4" />;
    if (name.includes('review') || name.includes('planning')) return <Users className="h-4 w-4" />;
    if (name.includes('progress') || name.includes('development')) return <Wrench className="h-4 w-4" />;
    if (name.includes('completed') || name.includes('done')) return <CheckCircle2 className="h-4 w-4" />;
    return <AlertCircle className="h-4 w-4" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <>
        <NavBar />
        <div className="container mx-auto px-4 py-8 pt-24">
          <div className="space-y-6">
            {/* Header skeleton */}
            <div className="text-center space-y-4">
              <Skeleton className="h-8 w-48 mx-auto" />
              <Skeleton className="h-4 w-96 mx-auto" />
            </div>

            {/* Stats skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>

            {/* Columns skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-96" />
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Helmet>
        <title>Product Roadmap - Light Hunt</title>
        <meta 
          name="description" 
          content="See what we're working on and what's coming next. Our public roadmap shows the features and improvements planned for Light Hunt." 
        />
        <meta name="keywords" content="roadmap, features, development, Light Hunt, upcoming features" />
        <link rel="canonical" href={`${window.location.origin}/roadmap`} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Product Roadmap - Light Hunt" />
        <meta property="og:description" content="See what we're working on and what's coming next for Light Hunt." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/roadmap`} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Product Roadmap - Light Hunt" />
        <meta name="twitter:description" content="See what we're working on and what's coming next for Light Hunt." />
      </Helmet>

      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold">Product Roadmap</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See what we're working on and what's coming next. Our roadmap is driven by your feedback and our vision for the future of Light Hunt.
            </p>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold">{stats.total_items}</p>
                      <p className="text-sm text-muted-foreground">Total Items</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <Wrench className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="text-2xl font-bold">{stats.in_progress}</p>
                      <p className="text-sm text-muted-foreground">In Progress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold">{stats.completed}</p>
                      <p className="text-sm text-muted-foreground">Completed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Progress</p>
                      <p className="text-sm font-medium">{stats.completion_rate}%</p>
                    </div>
                    <Progress value={stats.completion_rate} className="h-2" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Roadmap Columns */}
          {board ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {board.columns.map((column) => (
                <Card key={column.id} className="flex flex-col h-fit">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      {getColumnIcon(column.name)}
                      <span style={{ color: column.color }}>{column.name}</span>
                      <Badge variant="outline" className="ml-auto">
                        {column.items.length}
                      </Badge>
                    </CardTitle>
                    {column.description && (
                      <p className="text-sm text-muted-foreground">{column.description}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {column.items.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                          style={{ backgroundColor: `${column.color}20` }}
                        >
                          {getColumnIcon(column.name)}
                        </div>
                        <p className="text-sm">No items yet</p>
                      </div>
                    ) : (
                      column.items.map((item) => (
                        <Card key={item.id} className="border border-border hover:shadow-xs transition-shadow">
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              {/* Category and Priority */}
                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="secondary"
                                  style={{ 
                                    backgroundColor: `${item.feedback_item.category.color}15`, 
                                    color: item.feedback_item.category.color 
                                  }}
                                  className="text-xs"
                                >
                                  {item.feedback_item.category.name}
                                </Badge>
                                {item.priority > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    P{item.priority}
                                  </Badge>
                                )}
                              </div>

                              {/* Title */}
                              <h4 className="font-semibold text-sm leading-tight line-clamp-2">
                                {item.feedback_item.title}
                              </h4>

                              {/* Description */}
                              {item.feedback_item.description && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {item.feedback_item.description}
                                </p>
                              )}

                              {/* Stats */}
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <TrendingUp className="h-3 w-3" />
                                    {item.feedback_item.vote_count}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" />
                                    {formatDate(item.feedback_item.created_at)}
                                  </div>
                                </div>
                              </div>

                              {/* Labels */}
                              {item.labels && item.labels.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {item.labels.slice(0, 2).map((label, index) => (
                                    <Badge key={index} variant="outline" className="text-xs">
                                      {label}
                                    </Badge>
                                  ))}
                                  {item.labels.length > 2 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{item.labels.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Roadmap Available</h3>
                <p className="text-muted-foreground">
                  The public roadmap is not currently available. Please check back later.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Footer */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-2">
                <h3 className="font-semibold">Have ideas or feedback?</h3>
                <p className="text-sm text-muted-foreground">
                  We'd love to hear from you! Submit your ideas and vote on features that matter to you.
                </p>
                <div className="flex justify-center gap-4 mt-4">
                  <a 
                    href="/feedback" 
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Lightbulb className="h-4 w-4" />
                    Submit Feedback
                  </a>
                  <a 
                    href="/changelog" 
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Calendar className="h-4 w-4" />
                    View Changelog
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Roadmap;